import { buildDeposit, formatCents, type ProcessorId } from '@shared/fees';

// SAB-582: checkout fee disclosure. Imports the SAME fees.ts the edge functions use,
// so the total shown here is provably the total charged (both call grossUpCents on the
// same schedule with the same credited amount).
//
// Ships DARK. VITE_DEPOSIT_FEE_MODE mirrors the server DEPOSIT_FEE_MODE secret and
// MUST be flipped in lockstep with it. Default 'absorb' = today's behaviour: the
// platform eats the fee, charge == credit, there is nothing to disclose, so this
// component renders null and the deposit screen is unchanged. Only when the mode is
// 'gross_up' (a deliberate, disclosed cutover) does it show the credited / fee / total
// breakdown. An unconfirmed processor schedule (aeropay, soap) makes buildDeposit
// throw, and we fall back to the absorb display rather than guess a number.

type FeeMode = 'absorb' | 'gross_up';

function feeMode(): FeeMode {
  const raw = (import.meta.env.VITE_DEPOSIT_FEE_MODE ?? '').toString().trim().toLowerCase();
  return raw === 'gross_up' ? 'gross_up' : 'absorb';
}

interface Props {
  /** The amount credited to the wallet, in integer cents (the player liability). */
  amountCents: number;
  processor?: ProcessorId;
}

export function DepositFeeDisclosure({ amountCents, processor = 'paypal' }: Props) {
  if (!Number.isInteger(amountCents) || amountCents <= 0) return null;
  if (feeMode() !== 'gross_up') return null; // absorb: nothing to disclose, display unchanged

  let feeCents = 0;
  let totalCents = amountCents;
  try {
    const b = buildDeposit(amountCents, processor);
    feeCents = b.grossCents - b.amountCents; // what the player pays on top of the credit
    totalCents = b.grossCents;
  } catch {
    // Unconfirmed schedule: do not invent a fee. Fall back to the absorb display.
    return null;
  }
  if (feeCents <= 0) return null;

  return (
    <div className="mt-3 space-y-1.5 text-[13px]">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">Added to your wallet</span>
        <span className="text-foreground tabular-nums">{formatCents(amountCents)}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">Processing fee</span>
        <span className="text-foreground tabular-nums">{formatCents(feeCents)}</span>
      </div>
      <div className="flex items-center justify-between font-semibold">
        <span className="text-foreground">Total charged</span>
        <span className="text-foreground tabular-nums">{formatCents(totalCents)}</span>
      </div>
    </div>
  );
}
